import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";
import { IconChevronLeft, IconCamera, IconBluetooth, IconTrash, IconPhoto } from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { defectApi } from "../api";
import { focusInputNoScroll } from "../utils/markingScan";
import {
  barIconActiveSx,
  barIconSx,
  camera,
  ds,
  dsText,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  toneColor,
  topBarSx,
} from "../theme/keeperTheme";

const SCANNER_STRIP_H = 88;
const CAMERA_H = 200;
const CRPT_DEBOUNCE_CAMERA_MS = 380;
const CRPT_DEBOUNCE_SCANNER_MS = 90;

const ZXING_HINTS = new Map();
ZXING_HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
]);

export default function DefectAcceptPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedRef = useRef("");

  const [inputMode, setInputMode] = useState("scanner");
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;

  const [step, setStep] = useState("scan");
  const [pending, setPending] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [scannedText, setScannedText] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [recent, setRecent] = useState([]);

  const loadHistory = useCallback(() => {
    return defectApi.history(30, 15).then((data) => {
      setRecent(data.recent || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadHistory());
  }, [registerToolbarRefresh, loadHistory]);

  useEffect(() => {
    if (inputMode !== "camera" || step !== "scan") return undefined;
    let stream;
    let cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraReady(true);
        setCameraError(null);
      } catch (e) {
        setCameraError(e?.message || "Нет доступа к камере");
        setCameraReady(false);
      }
    })();
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setCameraReady(false);
    };
  }, [inputMode, step]);

  useEffect(() => {
    if (inputMode !== "scanner" || step !== "scan") return undefined;
    const t = window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 100);
    return () => window.clearTimeout(t);
  }, [inputMode, step]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError || step !== "scan") return undefined;
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader(ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (!cancelled && result) setScannedText(result.getText());
      })
      .then((controls) => {
        if (!cancelled) scanControlsRef.current = controls;
        else controls.stop();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      scanControlsRef.current?.stop();
      scanControlsRef.current = null;
    };
  }, [inputMode, cameraReady, cameraError, step]);

  const resetPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const resetToScan = () => {
    setStep("scan");
    setPending(null);
    resetPhoto();
    setScannedText("");
    lastProcessedRef.current = "";
    if (scannerInputRef.current) scannerInputRef.current.value = "";
  };

  useEffect(() => {
    if (step !== "scan") return undefined;
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    const raw = scannedText.trim();
    if (!raw) return undefined;

    const debounceMs = inputMode === "scanner" ? CRPT_DEBOUNCE_SCANNER_MS : CRPT_DEBOUNCE_CAMERA_MS;
    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      if (lastProcessedRef.current === raw || busy) return;
      lastProcessedRef.current = raw;
      setBusy(true);
      setStatusMessage(null);
      defectApi
        .previewScan({ code: raw })
        .then((result) => {
          setPending({ code: raw, ...result });
          setStep("photo");
          setStatusTone("info");
          setStatusMessage(`Сфотографируйте повреждённый товар: ${result.product_name}`);
        })
        .catch((e) => {
          setStatusTone("error");
          setStatusMessage(e?.message || "Товар не найден");
          lastProcessedRef.current = "";
        })
        .finally(() => setBusy(false));
    }, debounceMs);

    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [scannedText, inputMode, busy, step]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleConfirmWriteoff = async () => {
    if (!pending?.code) return;
    if (!photoFile) {
      setStatusTone("error");
      setStatusMessage("Сделайте фото товара перед списанием.");
      return;
    }
    setBusy(true);
    setStatusMessage(null);
    try {
      const result = await defectApi.acceptScan({
        code: pending.code,
        quantity: 1,
        photo: photoFile,
      });
      setStatusTone("success");
      setStatusMessage(
        `−${result.quantity_written_off} шт. · ${result.product_name} · остаток ${result.stock_quantity}`,
      );
      resetToScan();
      void loadHistory();
    } catch (e) {
      setStatusTone("error");
      setStatusMessage(e?.message || "Ошибка списания брака");
    } finally {
      setBusy(false);
    }
  };

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    setScannedText("");
    lastProcessedRef.current = "";
    if (scannerInputRef.current) scannerInputRef.current.value = "";
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const v = (e.target.value || "").trim();
      e.target.value = "";
      if (v) setScannedText(v);
    }
  };

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton onClick={() => navigate("/")} sx={barIconSx} aria-label="Назад">
          <IconChevronLeft size={26} />
        </IconButton>
        <IconTrash size={22} style={{ color: dsText.danger }} />
        <Typography sx={{ color: ds.text, fontWeight: 700, fontSize: "1.0625rem" }}>
          Брак
        </Typography>
      </Box>

      {step === "scan" ? (
        <>
          <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, color: ds.text2, fontSize: "0.85rem" }}>
            Отсканируйте маркировку или штрихкод — затем нужно сфотографировать повреждённый товар и списать 1 шт.
          </Typography>
          <Box sx={{ px: 2, py: 1, display: "flex", gap: 1 }}>
            <IconButton
              onClick={toggleInputMode}
              sx={{
                ...(inputMode === "scanner" ? barIconActiveSx : barIconSx),
              }}
              aria-label="Сканер"
            >
              <IconBluetooth size={22} />
            </IconButton>
            <IconButton
              onClick={toggleInputMode}
              sx={{
                ...(inputMode === "camera" ? barIconActiveSx : barIconSx),
              }}
              aria-label="Камера"
            >
              <IconCamera size={22} />
            </IconButton>
          </Box>
          {inputMode === "camera" ? (
            <Box
              sx={{
                height: CAMERA_H,
                position: "relative",
                bgcolor: camera.bg,
                borderRadius: `${ds.radiusLg}px`,
                border: `1px solid ${ds.border2}`,
                overflow: "hidden",
                mx: 2,
              }}
            >
              {cameraError ? (
                <Typography sx={{ color: camera.errorText, p: 2, fontSize: "0.85rem" }}>{cameraError}</Typography>
              ) : (
                <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </Box>
          ) : (
            <Box sx={{ px: 2, height: SCANNER_STRIP_H, display: "flex", alignItems: "center" }}>
              <TextField
                inputRef={scannerInputRef}
                fullWidth
                size="small"
                placeholder="Сканируйте сюда…"
                onKeyDown={handleScannerKeyDown}
                autoComplete="off"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...scannerFieldSx["& .MuiOutlinedInput-root"],
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography sx={{ color: ds.text, fontWeight: 700, fontSize: "1rem", mb: 0.5 }}>
            {pending?.product_name || "Товар"}
          </Typography>
          <Typography sx={{ color: ds.text2, fontSize: "0.85rem", mb: 2 }}>
            Остаток: {pending?.stock_quantity ?? "—"} шт. · списание: 1 шт.
          </Typography>
          <Typography sx={{ color: ds.text, fontSize: "0.9rem", mb: 1.5 }}>
            Сделайте фото повреждённого товара
          </Typography>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handlePhotoChange}
          />
          <Button
            variant="outlined"
            fullWidth
            startIcon={<IconPhoto size={20} />}
            onClick={() => photoInputRef.current?.click()}
            sx={{
              color: dsText.danger,
              borderColor: ds.danger,
              bgcolor: ds.dangerSoft,
              "&:hover": { borderColor: ds.danger, bgcolor: ds.dangerSoft2 },
              mb: 2,
              py: 1.25,
            }}
          >
            {photoFile ? "Заменить фото" : "Сделать фото"}
          </Button>
          {photoPreview ? (
            <Box
              component="img"
              src={photoPreview}
              alt="Фото брака"
              sx={{
                width: "100%",
                maxHeight: 220,
                objectFit: "contain",
                borderRadius: `${ds.radiusLg}px`,
                bgcolor: ds.surface2,
                border: `1px solid ${ds.border}`,
                mb: 2,
              }}
            />
          ) : null}
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={busy || !photoFile}
            onClick={handleConfirmWriteoff}
            sx={{ mb: 1 }}
          >
            Списать брак (−1 шт.)
          </Button>
          <Button variant="text" fullWidth disabled={busy} onClick={resetToScan} sx={{ color: ds.text2 }}>
            Отмена, другой скан
          </Button>
        </Box>
      )}

      {busy ? (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress color="error" />
        </Box>
      ) : null}

      {statusMessage ? (
        <Typography
          sx={{
            px: 2,
            pt: 1.5,
            color: toneColor(statusTone),
            fontSize: "0.9rem",
            fontWeight: 500,
          }}
        >
          {statusMessage}
        </Typography>
      ) : null}

      <Typography sx={{ ...sectionLabelSx, px: 2, pt: 2, pb: 0.5 }}>
        ПОСЛЕДНИЕ СПИСАНИЯ
      </Typography>
      <List dense sx={{ flex: 1, overflow: "auto", px: 1 }}>
        {recent.length === 0 ? (
          <ListItem>
            <ListItemText primary="Пока нет записей" sx={{ color: ds.text2 }} />
          </ListItem>
        ) : (
          recent.map((row) => (
            <ListItem key={row.id} sx={{ borderBottom: `1px solid ${ds.border}` }}>
              <ListItemText
                primary={row.product_name || `Товар #${row.warehouse_product_id}`}
                secondary={`−${row.quantity} · остаток ${row.stock_after ?? "—"} · ${formatTime(row.created_at)}`}
                primaryTypographyProps={{ color: ds.text, fontSize: "0.9rem", fontWeight: 600 }}
                secondaryTypographyProps={{ color: ds.text2, fontSize: "0.75rem" }}
              />
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
